/**
 * Lost powerbanks by day (MUI LineChart, stacked area). Uses dashboard filters (no internal month/year).
 * `lostPowerBanks` from API is dayOfMonth → count (e.g. { "5": 2 }); YYYY-MM-DD keys also supported.
 */
import React, { useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

function getDaysInMonth(month, year) {
  const n = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: n }, (_, i) => String(i + 1));
}

/** Parse date key to { year, month (0-11), day } for comparison. Handles YYYY-MM-DD and ISO. */
function parseDateKey(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split(/[-/.]/);
  if (parts.length >= 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && m >= 0 && m <= 11 && !Number.isNaN(d)) {
      return { year: y, month: m, day: d };
    }
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

function buildSeriesFromLostPowerBanks(lostPowerBanks, month, year) {
  const days = new Date(year, month + 1, 0).getDate();
  const values = Array(days).fill(0);
  if (!lostPowerBanks || typeof lostPowerBanks !== 'object') {
    return { categories: getDaysInMonth(month, year), data: values };
  }
  Object.keys(lostPowerBanks).forEach((key) => {
    const raw = lostPowerBanks[key];
    const count = Number(raw);
    if (!Number.isFinite(count)) return;

    // API returns Map dayOfMonth → count, e.g. { "1": 2, "15": 1 } (not YYYY-MM-DD).
    const asDay = Number(key);
    if (Number.isInteger(asDay) && String(asDay) === String(key).trim() && asDay >= 1 && asDay <= days) {
      values[asDay - 1] = count;
      return;
    }

    const parsed = parseDateKey(key);
    if (parsed && parsed.year === year && parsed.month === month && parsed.day >= 1 && parsed.day <= days) {
      values[parsed.day - 1] = count;
    }
  });
  return {
    categories: getDaysInMonth(month, year),
    data: values,
  };
}

export default function LostPowerbanksLineChart({ selectedMonth, selectedYear, lostPowerBanks, title }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { categories, data } = useMemo(
    () => buildSeriesFromLostPowerBanks(lostPowerBanks, selectedMonth, selectedYear),
    [lostPowerBanks, selectedMonth, selectedYear],
  );

  const xAxisData = Array.isArray(categories) ? categories : [];
  const seriesData = Array.isArray(data) ? data : [];
  const colorPalette = [theme.palette.warning.main];

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" gutterBottom>
          {title ?? t('dashboard.lostPowerbanksByDay')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LineChart
            colors={colorPalette}
            xAxis={[
              {
                scaleType: 'point',
                data: xAxisData,
                tickInterval: (_, i) => (i + 1) % 5 === 0,
                height: 24,
              },
            ]}
            yAxis={[{ width: 50 }]}
            series={[
              {
                id: 'lost',
                label: t('dashboard.powerbanksLost'),
                data: seriesData,
                showMark: false,
                curve: 'linear',
                stack: 'total',
                area: true,
                stackOrder: 'ascending',
              },
            ]}
            height={280}
            margin={{ left: 0, right: 20, top: 20, bottom: 0 }}
            grid={{ vertical: false, horizontal: true }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
